import { Badge, Card, Group, Select, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { api, unwrap } from '../api.ts';
import { demoDevices } from '../demo.ts';
import { relativeTime } from '../format.ts';
import { DemoBadge, Empty, Failure, Loading, Page } from '../ui.tsx';

const KIND_LABEL = {
  light: 'Lights',
  shade: 'Shades',
  fin: 'Fins',
  thermostat: 'Thermostat',
  air_conditioning: 'Air conditioning',
} as const;

const READING_UNIT: Record<string, string> = { temperature: '°C', humidity: '%', co2: 'ppm', occupancy: '' };

export function DevicesPage() {
  const search = useSearch({ from: '/devices' });
  const navigate = useNavigate({ from: '/devices' });
  const buildings = useQuery({ queryKey: ['buildings'], queryFn: () => unwrap(api().GET('/v1/buildings')) });
  const buildingId = search.buildingId ?? buildings.data?.[0]?.id;
  const building = buildings.data?.find((candidate) => candidate.id === buildingId);
  const spaces = useQuery({
    queryKey: ['spaces', buildingId],
    enabled: Boolean(buildingId),
    queryFn: () =>
      unwrap(
        api().GET('/v1/buildings/{buildingId}/spaces', {
          params: { path: { buildingId: buildingId ?? '' } },
        }),
      ),
  });
  // Live readings come from the twin's state endpoint: readings posted to the HTTPS ingest.
  const state = useQuery({
    queryKey: ['twin-state', building?.code],
    enabled: Boolean(building),
    refetchInterval: 15_000,
    queryFn: () =>
      unwrap(
        api().GET('/v1/twin/{buildingCode}/state', {
          params: { path: { buildingCode: building?.code ?? '' } },
        }),
      ),
  });

  const devices = building && spaces.data ? demoDevices(building, spaces.data) : [];
  const sensed = (state.data?.places ?? []).filter((place) => Object.keys(place.readings).length > 0);
  const placeName = (code: string) => spaces.data?.find((space) => space.code === code)?.name ?? code;

  return (
    <Page
      title="Devices"
      description="Sensors reporting into the twin, and the room equipment people control from the app."
      actions={
        <Select
          aria-label="Building"
          data={buildings.data?.map((candidate) => ({ value: candidate.id, label: candidate.name })) ?? []}
          value={buildingId ?? null}
          onChange={(value) => value && void navigate({ search: { buildingId: value }, replace: true })}
          allowDeselect={false}
          w={240}
        />
      }
    >
      <Failure error={buildings.error ?? spaces.error} />
      <Card withBorder padding="lg">
        <Group justify="space-between" mb="md">
          <div>
            <Text fw={700}>Sensors</Text>
            <Text size="sm" c="dimmed">
              Latest reading per place, refreshed every 15 seconds.
            </Text>
          </div>
          <Badge variant="light" color={sensed.length > 0 ? 'green' : 'gray'}>
            {sensed.length} places reporting
          </Badge>
        </Group>
        {state.isPending && building ? <Loading /> : null}
        {state.error ? (
          <Text size="sm" c="dimmed">
            No twin for this building yet: publish a model and scene from the twin tools to see live readings.
          </Text>
        ) : null}
        {state.data && sensed.length === 0 ? (
          <Empty>No readings yet. Start the sensor emulator, or connect the building's gateway.</Empty>
        ) : null}
        {sensed.length > 0 ? (
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Place</Table.Th>
                  <Table.Th>Readings</Table.Th>
                  <Table.Th>Booking</Table.Th>
                  <Table.Th>Last seen</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sensed.map((place) => {
                  const latest = Object.values(place.readings)
                    .map((reading) => reading.at)
                    .toSorted()
                    .at(-1);
                  return (
                    <Table.Tr key={place.code}>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {placeName(place.code)}
                        </Text>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {place.code}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6}>
                          {Object.entries(place.readings).map(([metric, reading]) => (
                            <Badge key={metric} variant="default" radius="sm">
                              {metric} {Math.round(reading.value * 10) / 10}
                              {READING_UNIT[metric] ?? ''}
                            </Badge>
                          ))}
                        </Group>
                      </Table.Td>
                      <Table.Td>{place.booking.replace('_', ' ')}</Table.Td>
                      <Table.Td>{relativeTime(latest ?? null)}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : null}
      </Card>
      <Card withBorder padding="lg">
        <Group justify="space-between" mb="md">
          <div>
            <Text fw={700}>Room equipment</Text>
            <Text size="sm" c="dimmed">
              Lights, shades, fins and thermostats per room, as on the app's Room control screen.
            </Text>
          </div>
          <DemoBadge what="Room control" />
        </Group>
        {spaces.isPending && buildingId ? <Loading /> : null}
        {spaces.data && devices.length === 0 ? <Empty>No meeting rooms in this building.</Empty> : null}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {Object.entries(Object.groupBy(devices, (device) => device.placeCode)).map(([placeCode, items]) => (
            <Card key={placeCode} withBorder padding="sm" bg="gray.0">
              <Stack gap={6}>
                <Text fw={600} size="sm">
                  {placeName(placeCode)}
                </Text>
                <Group gap={6}>
                  {items?.map((device) => (
                    <Badge key={device.id} variant="light" color="ink" radius="sm">
                      {KIND_LABEL[device.kind]}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Card>
    </Page>
  );
}
